import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { axiosInstance } from "../App";
import { FileText, Plus, Trash2, Edit, ArrowLeft, BookTemplate, FolderOpen, Filter } from "lucide-react";
import { toast } from "sonner";

const TEMPLATE_CATEGORIES = [
  { value: "general", label: "Genel", icon: "📋", color: "bg-gray-100 text-gray-800" },
  { value: "development", label: "Yazılım Geliştirme", icon: "💻", color: "bg-blue-100 text-blue-800" },
  { value: "design", label: "Tasarım", icon: "🎨", color: "bg-purple-100 text-purple-800" },
  { value: "marketing", label: "Pazarlama", icon: "📢", color: "bg-green-100 text-green-800" },
  { value: "operations", label: "Operasyon", icon: "⚙️", color: "bg-orange-100 text-orange-800" },
  { value: "hr", label: "İnsan Kaynakları", icon: "👥", color: "bg-pink-100 text-pink-800" },
  { value: "finance", label: "Finans", icon: "💰", color: "bg-yellow-100 text-yellow-800" },
  { value: "support", label: "Destek", icon: "🎧", color: "bg-cyan-100 text-cyan-800" },
];

function Templates() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    title: "",
    task_description: "",
    priority: "medium",
    category: "general",
    tags: ""
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const response = await axiosInstance.get("/templates");
      setTemplates(response.data);
    } catch (error) {
      toast.error(t('templatesLoadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (template = null) => {
    if (template) {
      setEditingTemplate(template);
      setFormData({
        name: template.name,
        description: template.description || "",
        title: template.title,
        task_description: template.task_description || "",
        priority: template.priority,
        category: template.category || "general",
        tags: template.tags.join(", ")
      });
    } else {
      setEditingTemplate(null);
      setFormData({
        name: "",
        description: "",
        title: "",
        task_description: "",
        priority: "medium",
        category: "general",
        tags: ""
      });
    }
    setDialogOpen(true);
  };

  const getCategoryInfo = (categoryValue) => {
    return TEMPLATE_CATEGORIES.find(c => c.value === categoryValue) || TEMPLATE_CATEGORIES[0];
  };

  const filteredTemplates = selectedCategory === "all"
    ? templates
    : templates.filter(t => (t.category || "general") === selectedCategory);

  const handleSaveTemplate = async (e) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.title.trim()) {
      toast.error(t('templateNameTaskTitleRequired'));
      return;
    }

    setSaving(true);
    try {
      const payload = {
        name: formData.name,
        description: formData.description,
        title: formData.title,
        task_description: formData.task_description,
        priority: formData.priority,
        category: formData.category,
        tags: formData.tags.split(",").map(t => t.trim()).filter(t => t)
      };

      if (editingTemplate) {
        await axiosInstance.put(`/templates/${editingTemplate.id}`, payload);
        toast.success(t('templateUpdated'));
      } else {
        await axiosInstance.post("/templates", payload);
        toast.success(t('templateCreated'));
      }

      setDialogOpen(false);
      fetchTemplates();
    } catch (error) {
      toast.error(editingTemplate ? t('templateUpdateFailed') : t('templateCreationFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!confirm(t('confirmDeleteTemplate'))) return;

    try {
      await axiosInstance.delete(`/templates/${templateId}`);
      toast.success(t('templateDeleted'));
      fetchTemplates();
    } catch (error) {
      toast.error(t('templateDeleteFailed'));
    }
  };

  const getPriorityColor = (priority) => {
    const colors = {
      high: "bg-red-100 text-red-800",
      medium: "bg-yellow-100 text-yellow-800",
      low: "bg-green-100 text-green-800"
    };
    return colors[priority] || colors.medium;
  };

  const getPriorityLabel = (priority) => {
    const labels = { high: "Yüksek", medium: "Orta", low: "Düşük" };
    return labels[priority] || priority;
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <Button
            variant="ghost"
            onClick={() => navigate("/dashboard")}
            className="mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t('backToDashboard')}
          </Button>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <img 
                src="/milestora-logo-light.png" 
                alt="Milestora" 
                className="h-12 w-auto object-contain"
              />
              <div>
                <h1 className="text-2xl font-bold flex items-center gap-2">
                  <BookTemplate className="h-7 w-7" />
                  {t('taskTemplates')}
                </h1>
                <p className="text-gray-600 mt-1">
                  {t('saveTemplatesDesc')}
                </p>
              </div>
            </div>
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button onClick={() => handleOpenDialog()}>
                  <Plus className="h-4 w-4 mr-2" />
                  {t('newTemplate')}
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle>
                    {editingTemplate ? t('editTemplateTitle') : t('createNewTemplateTitle')}
                  </DialogTitle>
                  <DialogDescription>
                    {t('templateFormDesc')}
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleSaveTemplate} className="space-y-4">
                  <div className="space-y-2">
                    <Label>{t('templateNameLabel')}</Label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder={t('templateNamePlaceholder')}
                      required
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>{t('templateDescLabel')}</Label>
                      <Textarea
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        placeholder={t('templateDescPlaceholder')}
                        rows={2}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Kategori</Label>
                      <Select
                        value={formData.category}
                        onValueChange={(value) => setFormData({ ...formData, category: value })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {TEMPLATE_CATEGORIES.map((cat) => (
                            <SelectItem key={cat.value} value={cat.value}>
                              <span className="flex items-center gap-2">
                                <span>{cat.icon}</span>
                                <span>{cat.label}</span>
                              </span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>{t('taskTitleLabel')}</Label>
                    <Input
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      placeholder={t('taskTitlePlaceholder')}
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>{t('taskDescLabel')}</Label>
                    <Textarea
                      value={formData.task_description}
                      onChange={(e) => setFormData({ ...formData, task_description: e.target.value })}
                      placeholder={t('taskDescPlaceholder')}
                      rows={3}
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>{t('priorityLabel')}</Label>
                      <Select
                        value={formData.priority}
                        onValueChange={(value) => setFormData({ ...formData, priority: value })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="low">{t('low')}</SelectItem>
                          <SelectItem value="medium">{t('medium')}</SelectItem>
                          <SelectItem value="high">{t('high')}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label>{t('tagsLabel')}</Label>
                      <Input
                        value={formData.tags}
                        onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                        placeholder={t('tagsPlaceholder')}
                      />
                      <p className="text-xs text-gray-500">{t('separateWithCommas')}</p>
                    </div>
                  </div>

                  <div className="flex gap-2 pt-4">
                    <Button type="submit" disabled={saving} className="flex-1">
                      {saving ? t('saving') : editingTemplate ? t('updating') : t('creating')}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setDialogOpen(false)}
                      disabled={saving}
                    >
                      {t('cancel')}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Category Filter */}
        <div className="mb-6">
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={() => setSelectedCategory("all")}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                selectedCategory === "all"
                  ? "bg-blue-500 text-white"
                  : "bg-white text-gray-700 hover:bg-gray-100 border"
              }`}
            >
              <span className="flex items-center gap-2">
                <FolderOpen className="h-4 w-4" />
                Tümü ({templates.length})
              </span>
            </button>
            {TEMPLATE_CATEGORIES.map((cat) => {
              const count = templates.filter(t => (t.category || "general") === cat.value).length;
              if (count === 0) return null;
              return (
                <button
                  key={cat.value}
                  onClick={() => setSelectedCategory(cat.value)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    selectedCategory === cat.value
                      ? "bg-blue-500 text-white"
                      : `${cat.color} hover:opacity-80 border`
                  }`}
                >
                  <span className="flex items-center gap-2">
                    <span>{cat.icon}</span>
                    {cat.label} ({count})
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Templates Grid */}
        {loading ? (
          <div className="text-center py-12">
            <p className="text-gray-500">{t('loading')}</p>
          </div>
        ) : templates.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <FileText className="h-12 w-12 mx-auto text-gray-300 mb-4" />
              <h3 className="text-lg font-semibold mb-2">{t('noTemplatesYet')}</h3>
              <p className="text-gray-600 mb-4">
                {t('createFirstTemplate')}
              </p>
              <Button onClick={() => handleOpenDialog()}>
                <Plus className="h-4 w-4 mr-2" />
                {t('createFirstTemplate')}
              </Button>
            </CardContent>
          </Card>
        ) : filteredTemplates.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Filter className="h-12 w-12 mx-auto text-gray-300 mb-4" />
              <h3 className="text-lg font-semibold mb-2">Bu kategoride şablon yok</h3>
              <p className="text-gray-600 mb-4">
                Başka bir kategori seçin veya yeni şablon oluşturun.
              </p>
              <Button onClick={() => setSelectedCategory("all")} variant="outline">
                Tüm Şablonları Göster
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredTemplates.map((template) => {
              const categoryInfo = getCategoryInfo(template.category || "general");
              return (
              <Card key={template.id} className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge className={`${categoryInfo.color} text-xs`}>
                          {categoryInfo.icon} {categoryInfo.label}
                        </Badge>
                      </div>
                      <CardTitle className="text-lg flex items-center gap-2">
                        <FileText className="h-5 w-5 text-blue-500" />
                        {template.name}
                      </CardTitle>
                      {template.description && (
                        <CardDescription className="mt-1">
                          {template.description}
                        </CardDescription>
                      )}
                    </div>
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleOpenDialog(template)}
                        className="h-8 w-8"
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDeleteTemplate(template.id)}
                        className="h-8 w-8"
                      >
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div>
                      <p className="text-sm font-medium text-gray-700">{t('taskTitleColon')}</p>
                      <p className="text-sm text-gray-600">{template.title}</p>
                    </div>
                    {template.task_description && (
                      <div>
                        <p className="text-sm font-medium text-gray-700">{t('descriptionColon')}</p>
                        <p className="text-sm text-gray-600 line-clamp-2">
                          {template.task_description}
                        </p>
                      </div>
                    )}
                    <div className="flex items-center gap-2">
                      <Badge className={getPriorityColor(template.priority)} variant="secondary">
                        {getPriorityLabel(template.priority)}
                      </Badge>
                      {template.tags && template.tags.length > 0 && (
                        <div className="flex gap-1 flex-wrap">
                          {template.tags.slice(0, 2).map((tag, idx) => (
                            <Badge key={idx} variant="outline" className="text-xs">
                              #{tag}
                            </Badge>
                          ))}
                          {template.tags.length > 2 && (
                            <Badge variant="outline" className="text-xs">
                              +{template.tags.length - 2}
                            </Badge>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default Templates;
